/**
 * Renders workers as sprites, interpolating between the previous and current
 * simulation positions using the fixed-timestep `alpha` so motion is smooth at
 * 60 FPS even though the simulation ticks at 20 TPS. Sprites are created/removed
 * to track the live entity set. (Object pooling / instancing arrives in Sprint 7
 * for the 20k-unit target.)
 */
import { Container, Graphics, Sprite, RenderTexture, Texture } from 'pixi.js';
import type { Renderer } from 'pixi.js';
import { gridToWorld } from '@settlers/shared';
import type { EntityId } from '@settlers/shared';
import {
  CPosition,
  CRenderInterp,
  CWorker,
  WorkerState,
} from '@settlers/engine';
import type { GameMap, World } from '@settlers/engine';

const STATE_TINT: Record<WorkerState, number> = {
  [WorkerState.Idle]: 0x9fb0c4,
  [WorkerState.MovingToWork]: 0xf2e9d0,
  [WorkerState.Working]: 0xe0a24a,
  [WorkerState.Returning]: 0x8fd39a,
};

export class UnitRenderer {
  readonly stage = new Container();
  private readonly sprites = new Map<EntityId, Sprite>();
  private readonly texture: Texture;

  constructor(
    renderer: Renderer,
    private readonly world: World,
    private readonly map: GameMap,
  ) {
    this.stage.sortableChildren = true;
    this.texture = this.buildTexture(renderer);
  }

  private buildTexture(renderer: Renderer): Texture {
    const g = new Graphics();
    // Feet at (12, 26). Shadow, body, head — light tones so tinting reads well.
    g.ellipse(12, 26, 8, 3).fill({ color: 0x000000, alpha: 0.28 });
    g.roundRect(7, 10, 10, 15, 3).fill(0xdfe6ee);
    g.circle(12, 8, 5).fill(0xf0d9b8);
    const tex = RenderTexture.create({ width: 24, height: 30, resolution: 1 });
    renderer.render({ container: g, target: tex });
    g.destroy();
    return tex;
  }

  sync(alpha: number): void {
    const seen = new Set<EntityId>();
    for (const e of this.world.query(CPosition, CRenderInterp)) {
      const pos = this.world.get(e, CPosition);
      const interp = this.world.get(e, CRenderInterp);
      if (!pos || !interp) continue;
      seen.add(e);

      const ix = interp.prevX + (pos.x - interp.prevX) * alpha;
      const iy = interp.prevY + (pos.y - interp.prevY) * alpha;
      const tx = Math.round(ix);
      const ty = Math.round(iy);
      const elev = this.map.inBounds(tx, ty) ? this.map.elevationAt(tx, ty) : 0;
      const world = gridToWorld(ix, iy, elev);

      let sprite = this.sprites.get(e);
      if (!sprite) {
        sprite = new Sprite(this.texture);
        sprite.anchor.set(0.5, 0.86);
        this.stage.addChild(sprite);
        this.sprites.set(e, sprite);
      }
      sprite.position.set(world.x, world.y);
      sprite.zIndex = world.y;
      const worker = this.world.get(e, CWorker);
      sprite.tint = worker ? STATE_TINT[worker.state] : 0xffffff;
    }

    // Remove sprites whose entity is gone.
    for (const [e, sprite] of this.sprites) {
      if (!seen.has(e)) {
        sprite.destroy();
        this.sprites.delete(e);
      }
    }
  }

  get count(): number {
    return this.sprites.size;
  }

  destroy(): void {
    for (const s of this.sprites.values()) s.destroy();
    this.sprites.clear();
    this.texture.destroy(true);
    this.stage.destroy({ children: true });
  }
}
